// Importaciones de interfaces.
import { IEmployee } from '../interfaces/employee.interface';
import { IRole } from '../interfaces/role.interface';

// Importaciones de modelos.
import { Role } from './role.model';

/**
 * Clase que representa a un empleado.
 *
 * Esta clase contiene información relevante sobre un empleado, incluyendo
 * sus credenciales de acceso y el estado del empleado dentro de la aplicación.
 */
export class Employee implements IEmployee {
  /**
   * Identificador único del empleado.
   *
   * Este campo representa un identificador único asignado al empleado.
   * Puede ser un número o nulo si no está asignado.
   */
  id: number | null;

  /**
   * Rol del empleado.
   *
   * Este campo define el rol asignado al empleado.
   * El rol puede ser un objeto que implementa la interfaz `IRole`.
   */
  role: IRole;

  /**
   * Nombre de usuario del empleado.
   *
   * Este campo almacena el nombre de usuario utilizado para iniciar sesión.
   * Puede ser nulo si no se ha establecido.
   */
  username: string | null;

  /**
   * Correo electrónico del empleado.
   *
   * Este campo almacena la dirección de correo electrónico del empleado.
   * Puede ser nulo si no se ha establecido.
   */
  email: string | null;

  /**
   * Nombre para mostrar del empleado.
   *
   * Este campo define el nombre visible del empleado.
   * Es el nombre que se muestra en interfaces y pantallas. Puede ser nulo si no se ha definido.
   */
  displayName: string | null;

  /**
   * Contraseña del empleado.
   *
   * Este campo almacena la contraseña del empleado.
   * Puede ser nulo si no se ha establecido.
   */
  password: string | null;

  /**
   * Token del empleado.
   *
   * Este campo contiene el token de autenticación del empleado.
   * El token puede ser nulo si no se ha asignado.
   */
  token: string | null;

  /**
   * El estado del empleado, indicando si está habilitado o deshabilitado.
   * Puede ser `null` si no se especifica el estado.
   */
  disabled: number;

  /**
   * Indica si el empleado está seleccionado en una lista.
   *
   * El valor `true` significa que el empleado está seleccionado.
   * El valor `false` significa que el empleado no está seleccionado.
   */
  selected: boolean;

  /**
   * Fecha de modificación del empleado.
   *
   * Esta propiedad almacena la fecha en la que el empleado fue modificado.
   * Si no se ha asignado una fecha, el valor será `null`.
   */
  updatedAt: string | null;

  /**
   * Fecha de creación del empleado.
   *
   * Esta propiedad almacena la fecha en la que el empleado fue creado.
   * Si no se ha asignado una fecha, el valor será `null`.
   */
  createdAt: string | null;

  /**
   * Constructor de la clase que inicializa una instancia de IEmployee.
   *
   * @param {Partial<IEmployee>} employee - Un objeto parcial de IEmployee con las siguientes propiedades opcionales:
   *   - `id` (opcional): Identificador del empleado. Por defecto, `null`.
   *   - `role` (opcional): Rol del empleado. Por defecto, se inicializa con una nueva instancia de `Role()`.
   *   - `username` (opcional): Nombre de usuario. Por defecto, `null`.
   *   - `email` (opcional): Correo electrónico del empleado. Por defecto, `null`.
   *   - `displayName` (opcional): Nombre visible del empleado. Por defecto, `null`.
   *   - `password` (opcional): Contraseña del empleado. Por defecto, `null`.
   *   - `token` (opcional): Token de autenticación del empleado. Por defecto, `null`.
   *   - `disabled` (opcional): Indica si el empleado está deshabilitado (`0` activo, otro valor deshabilitado). Por defecto, `0`.
   *   - `selected` (opcional): Indica si el empleado está seleccionado (`true` o `false`). Por defecto, `false`.
   *   - `createdAt` (opcional): Fecha de creación del empleado. Por defecto, `null`.
   */
  constructor({
    id = null,
    role = new Role(),
    username = null,
    email = null,
    displayName = null,
    password = null,
    token = null,
    disabled = 0,
    selected = false,
    updatedAt = null,
    createdAt = null,
  }: Partial<IEmployee> = {}) {
    this.id = id;
    this.role = role;
    this.username = username;
    this.email = email;
    this.displayName = displayName;
    this.password = password;
    this.token = token;
    this.disabled = disabled;
    this.selected = selected;
    this.updatedAt = updatedAt;
    this.createdAt = createdAt;
  }
}
